// frontend/src/components/FirstChoiceForm.js

import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserFirstPreferences,
  updateUserFirstPreferences,
  getDepartmentTermCapacities,
  getCurrentAssignments,
  updateUserFlag,
  fetchPreferenceStage,
  savePreferenceStage,
} from '../services/api';
import PreferenceTable from './PreferenceTable';
import HospitalSidebar from './HospitalSidebar';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import { Button } from "../components/ui/button";
import '../styles/FirstChoiceForm.css';
import DuplicateOverlay from './DuplicateOverlay';
import {
  buildInitialPreferences,
  clonePreferences,
  ensurePreferenceRow,
  convertPreferenceSetToGrid,
  buildPreferencePayloads,
  mergeFixedCells,
} from '../utils/preferenceUtils';

// チュートリアルのステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="first-preference-table"]',
    title: '1次希望登録表へようこそ！',
    content: 'この表で1次抽選の希望を登録します。各クール（横軸）と希望順位（縦軸）のセルに、希望する病院・診療科をドラッグ＆ドロップで配置してください。',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="first-hospital-sidebar"]',
    title: '病院一覧',
    content: 'こちらに登録可能な病院と診療科の一覧が表示されています。ここから希望する項目を選んでドラッグしてください。',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour="first-department-item"]',
    title: '診療科をドラッグ',
    content: 'この診療科名をマウスでクリック＆ホールドして、左側の希望表にドラッグします。',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
  },
  {
    target: '[data-tour="first-preference-cell"]',
    title: 'ここにドロップ',
    content: 'ドラッグした診療科をこのセルにドロップして配置します。希望する順番でセルを埋めていきましょう。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="first-cycle-header"]',
    title: 'クール列',
    content: '各クールの列です。地域医療で配属が決まったクールは「固定」と表示され、編集できません。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '.first-choice-save-button',
    title: '登録を忘れずに！',
    content: '全ての希望を入力したら、必ず「登録する」ボタンを押して登録を完了してください。',
    placement: 'top',
    disableBeacon: true,
  },
];

// 定数の定義
const HOSPITAL_TYPE = 'hospital';
const PREFERENCE_TYPE = 'preference';
const INITIAL_PREFERENCES_COUNT = 20;
const CYCLES = ["第1クール", "第2クール", "第3クール", "第4クール", "第5クール", "第6クール", "第7クール"];
const FIXED_PREFERENCE_KEY = '第1希望';
const SHOW_MAINTENANCE_ONLY = false;
const MAINTENANCE_MESSAGE = 'メンテナンス中です';

const initialPreferences = buildInitialPreferences(INITIAL_PREFERENCES_COUNT, CYCLES);

const FirstChoiceForm = () => {
  const { user } = useAuth();
  const [hospitals, setHospitals] = useState({});
  const [preferences, setPreferences] = useState(initialPreferences);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState('');
  const [duplicates, setDuplicates] = useState({});
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [isLoading, setIsLoading] = useState(!SHOW_MAINTENANCE_ONLY);
  const [error, setError] = useState(null);
  const [institutionDepartments, setInstitutionDepartments] = useState([]);
  const [isRegistering, setIsRegistering] = useState(false);
  const [duplicatesExist, setDuplicatesExist] = useState(false);
  const [fixedCycles, setFixedCycles] = useState([]);

  const findDepartmentTermCapacity = (deptId, instId, termId) => {
    return institutionDepartments.find((dept) =>
      dept.department_id === deptId &&
      dept.institution_id === instId &&
      dept.term_id === termId
    );
  };

  const formatFetchedPreferences = (fetchedPreferences, departmentCapacities) => {
    const formatted = buildInitialPreferences(INITIAL_PREFERENCES_COUNT, CYCLES);
    if (
      fetchedPreferences &&
      typeof fetchedPreferences === 'object' &&
      Object.keys(fetchedPreferences).length > 0
    ) {
      Object.entries(fetchedPreferences).forEach(([priority, cycles]) => {
        const row = ensurePreferenceRow(formatted, priority, CYCLES);
        if (cycles && typeof cycles === 'object') {
          Object.entries(cycles).forEach(([cycle, value]) => {
            if (value) {
              const departmentTermCapacity = (departmentCapacities || []).find((dept) =>
                dept.department_id === value.department_id &&
                dept.institution_id === value.institution_id &&
                dept.term_id === value.term_id
              );

              const departmentTermCapacityId = departmentTermCapacity
                ? departmentTermCapacity.id
                : value.department_term_capacity_id;

              row[cycle] = {
                id: value.id,
                department_term_capacity_id: departmentTermCapacityId,
                department_id: value.department_id,
                institution_id: value.institution_id,
                display_name: value.display_name,
                capacity: departmentTermCapacity ? (departmentTermCapacity.capacity ?? 0) : 0,
                term_id: value.term_id,
              };
            } else {
              row[cycle] = '';
            }
          });
        }
      });
    }
    return formatted;
  };

  const checkForDuplicates = useCallback(() => {
    const newDuplicates = {};
    Object.entries(preferences).forEach(([preference, cycles]) => {
      Object.entries(cycles).forEach(([cycle, value]) => {
        if (value && value.department_term_capacity_id) {
          const id = value.department_term_capacity_id;
          if (!newDuplicates[cycle]) {
            newDuplicates[cycle] = {};
          }
          if (newDuplicates[cycle][id]) {
            newDuplicates[cycle][id].push(preference);
          } else {
            newDuplicates[cycle][id] = [preference];
          }
        }
      });
    });

    const duplicatesFound = {};
    let hasDuplicates = false;
    Object.entries(newDuplicates).forEach(([cycle, ids]) => {
      duplicatesFound[cycle] = Object.entries(ids)
        .filter(([, preferences]) => preferences.length > 1)
        .reduce((acc, [id, preferences]) => {
          acc[id] = preferences;
          return acc;
        }, {});
      if (Object.keys(duplicatesFound[cycle]).length > 0) {
        hasDuplicates = true;
      }
    });

    setDuplicates(duplicatesFound);
    setDuplicatesExist(hasDuplicates);
    return hasDuplicates;
  }, [preferences]);

  useEffect(() => {
    if (!user || SHOW_MAINTENANCE_ONLY) {
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const departmentTermCapacitiesResponse = await getDepartmentTermCapacities();

        if (!Array.isArray(departmentTermCapacitiesResponse)) {
          console.error('Invalid institution departments data format:', departmentTermCapacitiesResponse);
          setError('施設データの形式が正しくありません。');
          return;
        }

        setInstitutionDepartments(departmentTermCapacitiesResponse);

        const hospitalsData = departmentTermCapacitiesResponse.reduce((acc, item) => {
          if (!acc[item.institution_id]) {
            acc[item.institution_id] = {
              name: item.institution_name,
              departments: [],
            };
          }
          acc[item.institution_id].departments.push({
            id: item.id,
            department_id: item.department_id,
            display_name: item.display_name,
            capacity: item.capacity,
            term_id: item.term_id,
            department_term_capacity_id: item.id,
            institution_id: item.institution_id,
          });
          return acc;
        }, {});

        setHospitals(hospitalsData);

        let formattedPreferences = null;

        try {
          const unifiedResponse = await fetchPreferenceStage('FIRST');
          if (unifiedResponse?.preference_set) {
            formattedPreferences = convertPreferenceSetToGrid({
              preferenceSet: unifiedResponse.preference_set,
              cycles: CYCLES,
              preferenceCount: INITIAL_PREFERENCES_COUNT,
              departmentCapacities: departmentTermCapacitiesResponse,
            });
          }
        } catch (unifiedError) {
          console.error('Failed to load unified first preferences:', unifiedError);
        }

        if (!formattedPreferences) {
          const preferencesResponse = await getUserFirstPreferences(user.uid);
          if (preferencesResponse.preferences && Object.keys(preferencesResponse.preferences).length > 0) {
            formattedPreferences = formatFetchedPreferences(
              preferencesResponse.preferences,
              departmentTermCapacitiesResponse,
            );
          } else {
            formattedPreferences = buildInitialPreferences(INITIAL_PREFERENCES_COUNT, CYCLES);
          }
        }

        const assignmentsResponse = await getCurrentAssignments();
        const exchangeAssignments = assignmentsResponse.filter(
          (assignment) => assignment.assignment_stage === 'rural-exchange'
        );
        const ruralAssignments = assignmentsResponse.filter(
          (assignment) => assignment.assignment_stage === 'rural' || assignment.assignment_stage === '4' || assignment.assignment_stage === 4
        );
        const fixedAssignments = exchangeAssignments.length > 0 ? exchangeAssignments : ruralAssignments;

        const fixedCyclesList = fixedAssignments.map(
          (assignment) => `第${assignment.term_id}クール`
        );
        setFixedCycles(fixedCyclesList);

        let nextPreferences = clonePreferences(formattedPreferences, CYCLES);

        if (fixedAssignments.length > 0) {
          fixedAssignments.forEach((assignment) => {
            const termId = assignment.term_id;
            const cycle = `第${termId}クール`;
            const row = ensurePreferenceRow(nextPreferences, FIXED_PREFERENCE_KEY, CYCLES);
            row[cycle] = {
              id: assignment.department_term_capacity_id,
              department_term_capacity_id: assignment.department_term_capacity_id,
              display_name: `${assignment.institution_name}・${assignment.department_name}`,
              capacity: null,
              term_id: termId,
              department_id: assignment.department_id,
              institution_id: assignment.institution_id,
              isFixed: true,
            };
          });
        }

        setPreferences(nextPreferences);
      } catch (fetchError) {
        console.error('Failed to fetch data:', fetchError);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    checkForDuplicates();
  }, [preferences, checkForDuplicates]);

  const onDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveItem(active.data.current.value);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);

    if (!over) return;

    const [sourceType, sourcePreference, sourceCycle] = active.id.split('-');
    const [destType, destPreference, destCycle] = over.id.split('-');

    if (fixedCycles.includes(destCycle)) {
      alert('このクールは固定されており、編集できません。');
      return;
    }

    const nextPreferences = clonePreferences(preferences, CYCLES);

    if (sourceType === HOSPITAL_TYPE && destType === PREFERENCE_TYPE) {
      const destRow = ensurePreferenceRow(nextPreferences, destPreference, CYCLES);

      if (preferences[destPreference]?.[destCycle]?.isFixed) {
        alert('このセルは固定されており、編集できません。');
        return;
      }

      const termId = CYCLES.indexOf(destCycle) + 1;
      const departmentTermCapacity = findDepartmentTermCapacity(
        active.data.current.value.department_id,
        active.data.current.value.institution_id,
        termId,
      );

      if (!departmentTermCapacity || (departmentTermCapacity.capacity ?? 0) <= 0) {
        alert(`${active.data.current.value.display_name}は配属枠がありません。この選択は保存されません。`);
        return;
      }

      destRow[destCycle] = {
        id: departmentTermCapacity.id,
        department_term_capacity_id: departmentTermCapacity.id,
        display_name: departmentTermCapacity.display_name,
        capacity: departmentTermCapacity.capacity ?? 0,
        term_id: termId,
        department_id: departmentTermCapacity.department_id,
        institution_id: departmentTermCapacity.institution_id,
      };
    } else if (sourceType === PREFERENCE_TYPE && destType === PREFERENCE_TYPE) {
      if (fixedCycles.includes(sourceCycle)) {
        alert('固定されたクールからは移動できません。');
        return;
      }

      const sourceRow = ensurePreferenceRow(nextPreferences, sourcePreference, CYCLES);
      const destRow = ensurePreferenceRow(nextPreferences, destPreference, CYCLES);

      const sourceValue = sourceRow[sourceCycle];
      const destValue = destRow[destCycle];

      if (sourceValue?.isFixed || destValue?.isFixed) {
        alert('固定されたセルは編集できません。');
        return;
      }

      if (sourceValue) {
        if (sourceCycle !== destCycle) {
          const newTermId = CYCLES.indexOf(destCycle) + 1;
          const departmentTermCapacity = findDepartmentTermCapacity(
            sourceValue.department_id,
            sourceValue.institution_id,
            newTermId,
          );

          if (!departmentTermCapacity || (departmentTermCapacity.capacity ?? 0) <= 0) {
            alert(`${sourceValue.display_name}は${destCycle}に配属枠がありません。この選択は保存されません。`);
            return;
          }

          const newSourceValue = {
            ...sourceValue,
            department_term_capacity_id: departmentTermCapacity.id,
            capacity: departmentTermCapacity.capacity ?? 0,
            term_id: newTermId,
          };

          sourceRow[sourceCycle] = destValue;
          destRow[destCycle] = newSourceValue;
        } else {
          sourceRow[sourceCycle] = destValue;
          destRow[destCycle] = sourceValue;
        }
      } else {
        sourceRow[sourceCycle] = destValue;
        destRow[destCycle] = sourceValue;
      }
    } else {
      return;
    }

    setPreferences(nextPreferences);
    checkForDuplicates();
  };

  const handleRemove = (preference, cycle) => {
    if (fixedCycles.includes(cycle)) {
      alert('このクールは固定されており、編集できません。');
      return;
    }
    if (preferences[preference]?.[cycle]?.isFixed) {
      alert('固定されたセルは削除できません。');
      return;
    }
    const nextPreferences = clonePreferences(preferences, CYCLES);
    const row = ensurePreferenceRow(nextPreferences, preference, CYCLES);
    row[cycle] = '';
    setPreferences(nextPreferences);
    checkForDuplicates();
  };

  const handleDrop = (item, preference, cycle) => {
    if (fixedCycles.includes(cycle)) {
      alert('このクールは固定されており、編集できません。');
      return;
    }
    if (preferences[preference]?.[cycle]?.isFixed) {
      alert('このセルは固定されており、編集できません。');
      return;
    }

    const nextPreferences = clonePreferences(preferences, CYCLES);
    const row = ensurePreferenceRow(nextPreferences, preference, CYCLES);

    const termId = CYCLES.indexOf(cycle) + 1;
    const departmentTermCapacity = findDepartmentTermCapacity(
      item.department_id,
      item.institution_id,
      termId
    );
    const capacity = departmentTermCapacity ? (departmentTermCapacity.capacity ?? 0) : 0;

    if (capacity <= 0) {
      alert(`${item.display_name}は配属枠がありません。この選択は保存されません。`);
      return;
    }

    row[cycle] = {
      id: departmentTermCapacity.id,
      department_term_capacity_id: departmentTermCapacity.id,
      display_name: departmentTermCapacity.display_name,
      capacity,
      term_id: termId,
      department_id: departmentTermCapacity.department_id,
      institution_id: departmentTermCapacity.institution_id,
    };
    setPreferences(nextPreferences);
    checkForDuplicates();
  };

  const handleSave = async () => {
    setIsRegistering(true);

    const hasDuplicates = checkForDuplicates();
    if (hasDuplicates) {
      alert('重複している希望があります。修正してください。');
      setIsRegistering(false);
      return;
    }

    // 未入力チェック
    const nonFixedCycles = CYCLES.filter(cycle => !fixedCycles.includes(cycle));
    let hasEmptyCells = false;

    for (const cycle of nonFixedCycles) {
      for (let i = 1; i <= INITIAL_PREFERENCES_COUNT; i++) {
        const priorityKey = `第${i}希望`;
        const cell = preferences[priorityKey]?.[cycle];
        if (!cell || (!cell.department_term_capacity_id && !cell.id)) {
          hasEmptyCells = true;
          break;
        }
      }
      if (hasEmptyCells) break;
    }

    if (hasEmptyCells) {
      const confirmed = window.confirm("すべての希望順位が登録されていません。\n未入力の箇所は、大学病院の枠からランダムに割り当てられますが、よろしいですか？");
      if (!confirmed) {
        setIsRegistering(false);
        return;
      }
    }

    try {
      const { unifiedItems, legacyPayload } = buildPreferencePayloads({
        preferences,
        cycles: CYCLES,
        candidateType: 'STANDARD',
        userId: user.uid,
      });

      if (unifiedItems.length === 0) {
        alert('希望が選択されていません。');
        setIsRegistering(false);
        return;
      }

      await savePreferenceStage('FIRST', {
        stage_type: 'FIRST',
        items: unifiedItems,
      });

      const response = await updateUserFirstPreferences(
        user.uid,
        legacyPayload
      );

      if (
        response.invalid_preferences &&
        response.invalid_preferences.length > 0
      ) {
        highlightInvalidPreferences(response.invalid_preferences);
        alert(
          'サーバー側で配属枠の問題が検出されました。紫色でハイライトされた項目を確認してください。'
        );
      } else {
        alert('希望が保存されました');

        let nextPreferences;
        if (response.preferences && Object.keys(response.preferences).length > 0) {
          nextPreferences = formatFetchedPreferences(response.preferences, institutionDepartments);
        } else {
          nextPreferences = buildInitialPreferences(INITIAL_PREFERENCES_COUNT, CYCLES);
        }

        const merged = mergeFixedCells({
          currentPreferences: preferences,
          nextPreferences,
          cycles: CYCLES,
        });

        setPreferences(merged);

        await updateUserFlag(user.uid, 'first_preference_status', true);
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.detail
      ) {
        alert(
          '希望の保存に失敗しました: ' +
          JSON.stringify(error.response.data.detail)
        );
      } else {
        alert(
          '希望の保存に失敗しました。時間をおいて再度試してください。エラーが続く場合はスクリーンショットをLINEにて送信してください。'
        );
      }
    } finally {
      setIsRegistering(false);
    }
  };

  // 無効な希望のハイライト処理
  const highlightInvalidPreferences = (invalidPreferences) => {
    invalidPreferences.forEach((pref) => {
      const { department_term_capacity_id, preference_order } = pref;
      const element = document.querySelector(
        `[data-department-id="${department_term_capacity_id}"][data-preference-order="${preference_order}"]`
      );
      if (element) {
        element.classList.add('invalid-preference');
      }
    });
  };

  // サイドバーの切り替え処理
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // ローディング状態の表示
  if (isLoading) return <LoadingSpinner text="データを読み込んでいます..." />;

  // エラーメッセージの表示
  if (error) return (
    <div className="flex justify-center items-center h-screen bg-background">
      <div className="text-accent text-xl" role="alert">{error}</div>
    </div>
  );

  // ユーザーがログインしていない場合のリダイレクト
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (SHOW_MAINTENANCE_ONLY) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <div className="text-accent text-xl" role="status">{MAINTENANCE_MESSAGE}</div>
      </div>
    );
  }

  return (
    <div className="first-choice-container first-choice-form-container">
      {/* オンボーディングツアー */}
      <OnboardingTour
        tourKey="first-choice"
        steps={TOUR_STEPS}
        isReady={!isLoading && Object.keys(hospitals).length > 0}
      />

      <header className={`first-choice-header ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <h2 className="first-choice-title">第1次抽選希望登録</h2>
        <button
          type="button"
          className={`first-choice-tour-button ${isSidebarOpen ? 'sidebar-open' : ''}`}
          onClick={() => {
            try {
              localStorage.removeItem('tour-completed-first-choice');
            } catch { }
            window['first-choiceTour']?.startTour?.();
          }}
          title="操作ガイドを再表示"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>操作ガイド</span>
        </button>
      </header>
      {Object.keys(hospitals).length === 0 ? (
        <div className="first-choice-empty-state">施設データが利用できません</div>
      ) : (
        <div className="first-choice-content">
          <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div className={`first-choice-board ${isSidebarOpen ? 'sidebar-open' : ''}`}>
              <PreferenceTable
                preferences={preferences}
                isSidebarOpen={isSidebarOpen}
                onRemove={handleRemove}
                onDrop={handleDrop}
                duplicates={duplicates}
                departmentTermCapacities={institutionDepartments}
                fixedCycles={fixedCycles}
              />
              <HospitalSidebar
                hospitals={hospitals}
                isOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
              />
            </div>
            {duplicatesExist && (
              <div className="first-choice-alert" role="alert">
                重複している希望があります。修正してください。
              </div>
            )}
            <div className="first-choice-actions">
              <Button
                onClick={handleSave}
                className="first-choice-save-button"
                disabled={isRegistering || duplicatesExist}
              >
                {isRegistering ? '登録中...' : '登録する'}
              </Button>
            </div>
            <DragOverlay>
              {activeId ? (
                <div className="first-choice-drag-preview">
                  {typeof activeItem === 'object' ? activeItem.display_name : activeItem}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}
      {showDuplicates && <DuplicateOverlay duplicates={duplicates} onClose={() => setShowDuplicates(false)} />}
    </div>
  );
};

export default FirstChoiceForm;
